import { showErrorToast, AsyncButton } from '@bpinternal/ui-kit-next'
import { useRouter } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useEffect, useRef, type FC } from 'react'
import { useAsyncSetWorkspacePreferences } from '~/hooks'
import { trackEvent } from '~/providers'
import { AnalyticEvent } from '~/providers/SegmentProvider'
import { getApiClient } from '~/stores'
import { getUseBotOnboardingStore } from '../../stores/useBotOnboardingStore'
import { getUseRichTextSourceStore } from '../../stores/useRichTextSource'
import { useStepNavStore } from '../../stores/useStepNavStore'
import { BotDescriptionStep } from './BotDescriptionStep'
import { BotNameStep } from './BotNameStep'
import { BotUseCaseStep } from './BotUseCaseStep'
import { BusinessStep } from './BusinessStep'
import { DeploymentStep } from './DeploymentStep'
import { KnowledgeStep } from './KnowledgeStep/KnowledgeStep'
import { StepperProgressBar } from './StepperProgressCard'

type Props = {
  botId: string
  workspaceId: string
}

export type StepComponentHandle = {
  handleNext: () => Promise<void>
}

export const CreateBotOnboarding: FC<Props> = ({ botId, workspaceId }) => {
  const steps = ['Bot Name', 'Use Case', 'Description', 'Business', 'Knowledge', 'Deploy']

  const setAsyncWorkspacePreference = useAsyncSetWorkspacePreferences(workspaceId)

  const useBotOnboardingStore = getUseBotOnboardingStore({ botId, workspaceId })
  const onboardingStep = useBotOnboardingStore((state) => state.onboardingStep)
  const useCaseId = useBotOnboardingStore((state) => state.useCaseId)
  const business = useBotOnboardingStore((state) => state.business)
  const knowledgeBase = useBotOnboardingStore((state) => state.knowledgeBase)
  const websitesChecked = useBotOnboardingStore((state) => state.websiteUrlsTree.checked)
  const setOnboardingStep = useBotOnboardingStore((state) => state.setOnboardingStep)
  const identityStatus = useBotOnboardingStore((state) => state.identityStatus)
  const identityWorkflowId = useBotOnboardingStore((state) => state.identityWorkflowId)
  const continueIdentityWorflow = useBotOnboardingStore((state) => state.continueIdentityWorkflow)

  const useRichTextSource = getUseRichTextSourceStore({ botId, workspaceId })
  const knowledgeRichText = useRichTextSource((state) => state.content)

  const nextDisabled = useStepNavStore((state) => state.nextDisabled)
  const backDisabled = useStepNavStore((state) => state.backDisabled)
  const nextLoading = useStepNavStore((state) => state.nextLoading)

  const isIdentityContinued = useRef(false)
  const childRef = useRef<{ handleNext: () => Promise<void> }>(null)

  const { navigate } = useRouter()

  useEffect(() => {
    if (!isIdentityContinued.current) {
      continueIdentityWorflow()
      isIdentityContinued.current = true
    }

    if (onboardingStep !== 1) return
    trackEvent({
      type: 'onboarding_started',
      botId,
      workspaceId,
    })
  }, [])

  useEffect(() => {
    if (identityStatus === 'error') {
      showErrorToast('Something went wrong, please try again.')
      setOnboardingStep(4)
    }
  }, [identityStatus])

  const handleNext = async () => {
    try {
      await childRef.current?.handleNext()
    } catch (e: any) {
      showErrorToast(e.message)
      return
    }

    let nextStep = onboardingStep < steps.length ? onboardingStep + 1 : onboardingStep
    if (onboardingStep === 2 && useCaseId !== 'other') {
      nextStep++
    }

    setOnboardingStep(nextStep)
    handleEvents()
  }

  const handleEvents = () => {
    const eventInfo = trackEventList[onboardingStep]
    if (!Object.keys(trackEventList).includes(onboardingStep.toString()) || !eventInfo) return

    eventInfo.forEach((event) => trackEvent(event))
  }

  const trackEventList: Record<number, Array<AnalyticEvent>> = {
    1: [{ type: 'onboarding_name_bot', workspaceId, botId }],
    2: [{ type: 'onboarding_agent_type_selected', workspaceId, botId, useCase: useCaseId ?? 'other' }],
    4: [
      {
        type: 'onboarding_context_extraction',
        workspaceId,
        botId,
        source: business.websiteUrl.length > 0 ? 'website' : business.richText.length > 0 ? 'text' : 'none',
      },
    ],
    5: [
      {
        type: 'onboarding_knowledge_base_usage',
        workspaceId,
        botId,
        knowledgeType: 'urls',
        count: websitesChecked.length,
      },
      {
        type: 'onboarding_knowledge_base_usage',
        workspaceId,
        botId,
        knowledgeType: 'files',
        count: knowledgeBase.uploadableFiles.length,
      },
      {
        type: 'onboarding_knowledge_base_usage',
        workspaceId,
        botId,
        knowledgeType: 'richtext',
        count: knowledgeRichText.trim().length > 0 ? 1 : 0,
      },
    ],
  }

  const handlePrevious = () => {
    let prevStep = onboardingStep > 1 ? onboardingStep - 1 : onboardingStep
    if (onboardingStep === 3 || (onboardingStep === 4 && useCaseId !== 'other')) {
      prevStep--
    }
    handleCancel() // not necessary for the new onboarding flow
    setOnboardingStep(prevStep)
  }

  const handleCancel = () => {
    const apiClient = getApiClient({ workspaceId, botId })
    if (identityWorkflowId && identityStatus === 'in-progress') {
      apiClient.updateWorkflow({
        id: identityWorkflowId,
        status: 'cancelled',
      })
    }
  }

  const renderStep = () => {
    switch (onboardingStep) {
      case 1:
        return <BotNameStep key={onboardingStep} workspaceId={workspaceId} botId={botId} />
      case 2:
        return <BotUseCaseStep key={onboardingStep} workspaceId={workspaceId} botId={botId} />
      case 3:
        return <BotDescriptionStep key={onboardingStep} workspaceId={workspaceId} botId={botId} />
      case 4:
        return <BusinessStep ref={childRef} key={onboardingStep} workspaceId={workspaceId} botId={botId} />
      case 5:
        return <KnowledgeStep ref={childRef} key={onboardingStep} workspaceId={workspaceId} botId={botId} />
      case 6:
        return <DeploymentStep key={onboardingStep} />
      default:
        return null
    }
  }

  return (
    <div className="flex h-full flex-col lg:justify-center">
      <div
        className="flex h-full w-full flex-col gap-8 p-4 lg:mx-auto lg:h-[650px] lg:max-w-[40rem] lg:p-0"
        onKeyDown={(event) => {
          if (nextDisabled || nextLoading || onboardingStep === 5) return
          if (event.key === 'Enter') {
            handleNext()
          }
        }}
      >
        <StepperProgressBar
          className={onboardingStep > 5 ? 'opacity-0' : ''}
          backDisabled={backDisabled}
          onBackClicked={handlePrevious}
          onSkipClicked={async () => {
            await setAsyncWorkspacePreference({ path: 'isOnboarding/$botId', params: { botId }, value: false })
            trackEvent({ type: 'onboarding_skipped', botId, workspaceId })
            navigate({ to: '/workspaces/$workspaceId/bots/$botId', params: { workspaceId, botId } })
          }}
        />
        <div className="flex flex-1 overflow-auto p-px lg:overflow-visible lg:p-0">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              className="flex w-full flex-1"
              key={onboardingStep}
              initial={{ opacity: 0, scale: 0.96, filter: 'blur(3px)' }}
              animate={{ opacity: 1, scale: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, scale: 0.96, filter: 'blur(3px)' }}
              transition={{
                duration: 0.2,
              }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>
        {onboardingStep < 6 && (
          <AsyncButton
            className="h-12 w-full"
            onClick={handleNext}
            disabled={nextDisabled || nextLoading}
            loading={nextLoading}
          >
            {onboardingStep === 5 ? 'Train agent' : 'Next'}
            <ArrowRight />
          </AsyncButton>
        )}
      </div>
    </div>
  )
}
